"use client";

import { useMarqueeLoop } from "@/lib/animation";
import { TICKER_ITEM_DURATION } from "@/lib/constants";

const COPIES = 8;

interface HeaderMarqueeProps {
  text: string;
}

/**
 * Motion-driven infinite marquee — replaces the previous CSS keyframe
 * version which had a visible "tick" at the loop boundary.
 *
 * The track renders the text twice (`COPIES` × 2) and `useMarqueeLoop`
 * translates it `0% → -50%` on a linear repeat. At -50% the second copy
 * sits exactly where the first started, so the jump back to 0 paints the
 * same pixels — invisible to the eye, and driven every RAF tick so
 * there's no single-frame discontinuity at the boundary.
 *
 * Reduced motion → no animation; the track sits at 0%, showing the
 * ticker text repeated statically.
 */
export function HeaderMarquee({ text }: HeaderMarqueeProps) {
  const copies = Array.from({ length: COPIES }, (_, i) => i);

  // We render the content twice (`COPIES × 2` spans total) and animate
  // -50% of the doubled track. So traversing the *visible* COPIES at
  // `TICKER_ITEM_DURATION` per item means the full -50% pass takes
  // `TICKER_ITEM_DURATION × COPIES × 2`. The `× 2` matters — without it
  // the marquee runs at double the intended speed.
  const { scope } = useMarqueeLoop({
    duration: TICKER_ITEM_DURATION * COPIES * 2,
  });

  return (
    <div className="bg-copper-kettle overflow-hidden">
      <span className="sr-only">{text}</span>
      <div
        ref={scope}
        aria-hidden="true"
        className="flex w-max py-2.5 whitespace-nowrap"
      >
        {copies.map((i) => (
          <span
            key={i}
            className="inline-block shrink-0 px-8 font-mono text-sm text-white"
          >
            {text}
          </span>
        ))}
        {copies.map((i) => (
          <span
            key={`d${i}`}
            className="inline-block shrink-0 px-8 font-mono text-sm text-white"
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}
